// AGC: Admin Panel - Section Card Component
// AGC: Collapsible container for grouping related configuration options

import React, { useState } from 'react'

interface SectionCardProps {
  title: string
  icon: string
  description?: string
  children: React.ReactNode
  collapsible?: boolean
  defaultExpanded?: boolean
}

/**
 * AGC: SectionCard - Expandable section container
 * AGC: Groups related configuration options with optional collapse functionality
 */
export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  icon,
  description,
  children,
  collapsible = false,
  defaultExpanded = true,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <div className="bg-white dark:bg-admin-card-dark border border-slate-200 dark:border-white/5 rounded-xl mb-6 shadow-card dark:shadow-none overflow-hidden">
      <div
        className={`px-6 py-5 flex justify-between items-center ${
          expanded ? 'border-b border-slate-100 dark:border-white/5' : ''
        } ${collapsible ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-white/[0.02] active:ring-2 active:ring-[var(--ring-click-primary)]' : ''}`}
        onClick={() => collapsible && setExpanded(!expanded)}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onKeyDown={collapsible ? (e) => e.key === 'Enter' && setExpanded((x) => !x) : undefined}
      >
        <div>
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2.5">
            <span className="text-xl">{icon}</span>
            {title}
          </h3>
          {description && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{description}</p>
          )}
        </div>
        {collapsible && (
          <span className="text-slate-400 dark:text-slate-500 text-xl">
            {expanded ? '▼' : '▶'}
          </span>
        )}
      </div>
      {expanded && <div className="p-6">{children}</div>}
    </div>
  )
}

export default SectionCard


